import { Component, HostListener, Input, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, environment, HelperService, LanguageService, ProductService, StorageService, StorageKeys, UserService } from "@mypxplat/xplat/core";
import { AppService, AvoService, BetaService, WebCommunityService } from "@mypxplat/xplat/web/core";

import { filter, take } from "rxjs/operators";
import { Router } from "@angular/router";
import { BetaEnrollComponent, FeatureUpdatesComponent, GenericAlertComponent, LinkFenderAccountComponent, SubmitFeedbackComponent, ThemeChooserComponent } from "../modals";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { signOut } from "aws-amplify/auth";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-cloud-nav-overlay",
  templateUrl: "cloud-nav-overlay.component.html",
  standalone: false,
})
export class CloudNavOverlayComponent extends BaseComponent {
  @Input() admin: boolean = false;
  @Input() fender: boolean = false;
  /** Exposed for the template so community nav links can route to `/communities` (multi-community landing) when the flag is on. */
  public environment = environment;
  public isBetaManager: boolean;
  public themes: Array<any> = [
    { name: "Auto", id: "auto", translateKey: "com.auto_theme" },
    { name: "Dark", id: "plaindark", translateKey: "com.dark_theme" },
    { name: "Light", id: "plainlight", translateKey: "com.light_theme" },
  ];
  public selectedTheme = "auto";
  readonly featureRequestsUrl: string;
  public osanoAvailable: boolean;
  constructor(
    userService: UserService,
    private _storage: StorageService,
    public appService: AppService,
    public helperService: HelperService,
    public languageService: LanguageService,
    public router: Router,
    public betaService: BetaService,
    public db: AngularFirestore,
    public fbAuth: AngularFireAuth,
    public productService: ProductService,
    public communityService: WebCommunityService,
    private translate: TranslateService,
    private avoService: AvoService
  ) {
    super(userService);
    this.featureRequestsUrl = this.env.fenderFeatureRequestsUrl;
  }

  /**
   * Fire Navigation Clicked from the drawer (hamburger menu). Only runs when fender === true.
   * navigationCore is "drawer" to distinguish from header nav.
   */
  trackDrawerNavigation(navigationLabel: string) {
    if (!this.fender) return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (!te || typeof te.navigationClicked !== "function") return;
        const page = this.avoService.getPageProperties({ pageName: "Navigation" });
        const extra = {
          navigationType: "main" as const,
          navigationLabel,
          navigationCore: "drawer",
          navigationGroup: null as string | null,
          navigationFlyout: null as string | null,
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.navigationClicked(payload);
      });
  }

  ngOnInit() {
    if (this.betaService.enrolledBetaFeatures && this.betaService.enrolledBetaFeatures.length && this.betaService.enrolledBetaFeatures.filter((item: any) => item.manager).length) {
      this.isBetaManager = true;
    }
    this.selectedTheme = this.appService.selectedTheme;
    this.themes.forEach((item) => (item.id == this.selectedTheme ? (item.selected = true) : (item.selected = false)));
    const keys = this.themes.map((t) => t.translateKey);

    this.translate
      .stream(keys)
      .pipe(takeUntil(this.destroy$))
      .subscribe((dict) => {
        this.themes = this.themes.map((t) => ({
          ...t,
          name: dict[t.translateKey],
        }));
      });

    if ((<any>window).Osano?.cm?.showDrawer) {
      this.osanoAvailable = true;
    }
  }

  manageCookies() {
    if ((<any>window).Osano?.cm?.showDrawer) {
      this.trackDrawerNavigation("Cookie Preferences");
      this.close(false, true);
      (<any>window).Osano.cm.showDrawer("osano-cm-dom-info-dialog-open");
    } else {
      this.close(false, true);
      let modalRef = this.appService.showModal(GenericAlertComponent, { size: "lg" });
      modalRef.componentInstance.html =
        "Our cookie management tool was not able to load. If you are using a browser extension that blocks scripts, please disable it for this site and try again. If you continue to have issues, please contact support.";
      modalRef.componentInstance.submitText = "Ok";
    }
  }

  selectTheme(id) {
    this.appService.initTheme(id);
    this.selectedTheme = this.appService.selectedTheme;
    this.themes.forEach((item) => (item.id == this.selectedTheme ? (item.selected = true) : (item.selected = false)));
    if (this.user) {
      this.userService.saveUser({ id: this.user.id, theme: id }).subscribe((result) => {});
    }
  }

  linkFenderAccount() {
    this.close(false, true);
    const modalRef = this.appService.showModal(LinkFenderAccountComponent, { size: "lg" });
  }

  @HostListener("document:keydown.escape")
  closeOnEscape() {
    if (this.appService.showNavOverlay) {
      this.closeMenu();
    }
  }

  closeMenu() {
    this.appService.showNavOverlay = false;
    setTimeout(() => document.getElementById("header-menu-button")?.focus(), 0);
  }

  close(args, force?) {
    if ((args && args.target.className == "cloud-nav-overlay") || (args && args.target.className == "cloud-nav-positioner") || force) {
      this.closeMenu();
    }
  }

  openSubmitFeedback() {
    const modalRef = this.appService.showModal(SubmitFeedbackComponent, { size: "lg" });
  }

  openBeta() {
    const modalRef = this.appService.showModal(BetaEnrollComponent, { size: "lg" });
  }

  viewAllAnnouncements() {
    let modal = this.appService.showModal(FeatureUpdatesComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modal.componentInstance.showingAll = true;
  }

  showPrivacySettings() {
    (<any>window).UC_UI.showSecondLayer();
  }

  openUrl(url) {
    window.open(url);
  }

  showThemeChooser() {
    this.appService.showThemeChooser(ThemeChooserComponent);
  }

  async logout() {
    const isPublicRoute = this._isCurrentRoutePublic();
    await signOut();
    this.appService.logout(true).subscribe(() => {
      this.appService.hasInittedApp = false;
      if (isPublicRoute) {
        // The current page is one guests can view — reload keeps them here in guest mode.
        window.location.reload();
      } else {
        // Auth-required page (e.g. /products/detail/:id, /account, /workspaces detail).
        // `/home` is itself a public route now, so falling back to it would drop the user
        // into guest-home instead of the login form. Hand off to `redirectToConnect` which
        // performs the canonical logout-and-redirect-to-login with a return-to param.
        this.appService.redirectToConnect(true, false);
      }
    });
  }

  private _isCurrentRoutePublic(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    return !!route.data?.["public"];
  }

  /**
   * Same rule as the header: community shows for authed users always, guests only when
   * communityPublicAccess is on. Mirrors CommunityPublicRouteGuard's decision so we don't
   * offer a link that would just bounce to login.
   */
  get showCommunityLink(): boolean {
    if (this.user) return true;
    return !!(environment.features as any).communityPublicAccess;
  }

  signIn() {
    this.appService.redirectToConnect(true, false);
  }

  ngOnDestroy() {
    this.appService.showNavOverlay = false;
    this.closeMenu();
  }
}
